// The "Source of Wealth & Funds" questions of the fact find (SEP-257 item 4).
//
// The onboarding form of a person and the onboarding form of an entity ask
// the same questions with the same words (`suitability-fact-find.pdf`,
// `entity-fact-find.pdf`). Each new fact find of an onboarding asks them on
// the "Existing Clients" form or on the entity form of SEP-351 (Dowgate
// decision of 2026-10-21). The web form, the server field lists and the entity
// form read this one definition, so the words cannot drift.
//
// A follow-up pair belongs to ONE ticked source. Each pair depends on its own
// option of `sow_types`. The two country questions are about the whole source
// of wealth, so they have no condition.

/** One question of the section. The shape is a subset of the web `FactFindQuestion`. */
export interface SourceOfWealthQuestion {
    readonly id: string;
    readonly type: 'multi_select' | 'long_text' | 'country_select';
    readonly label: string;
    readonly why: string;
    readonly options?: string[];
    readonly dependsOn?: { questionId: string; equals: string[] };
}

/** The section id of the questions on each form. */
export const SOURCE_OF_WEALTH_SECTION_ID = 'source_of_wealth';

export const SOURCE_OF_WEALTH_TITLE = 'Source of Wealth & Funds';

export const SOURCE_OF_WEALTH_DESCRIPTION = 'Origin of the wealth and the funds being invested.';

/** The paragraph above the section. The paper forms of a person and of an entity print the same words (SEP-318). */
export const SOURCE_OF_WEALTH_INTRO =
    'As part of our regulatory obligations, we need to understand both the source of your overall wealth (how you accumulated your wealth over time) and the specific source of funds being invested with us. For inheritance or business-related funds, we require additional details to complete our understanding. This includes information about how the inherited wealth was originally generated or details about the business activities that generated the funds.';

/** The sources of wealth, in the order of the form. */
export const SOURCE_OF_WEALTH_TYPES = [
    'Employment / Business',
    'Savings & Investments',
    'Inheritance / Gifts',
    'Property Sale',
    'Other',
] as const;

const WEALTH_WHY = 'A clear narrative supports our anti-money-laundering obligations.';
const FUNDS_WHY = 'We must evidence where the specific funds being invested came from.';

/** The wealth and the funds questions of one ticked source. */
function sourcePair(
    key: string,
    subject: string,
    option: (typeof SOURCE_OF_WEALTH_TYPES)[number],
): SourceOfWealthQuestion[] {
    const dependsOn = { questionId: 'sow_types', equals: [option] };
    return [
        {
            id: `sow_${key}_wealth_detail`,
            type: 'long_text',
            label: `${subject} — how was this wealth accumulated?`,
            why: WEALTH_WHY,
            dependsOn,
        },
        {
            id: `sow_${key}_funds_detail`,
            type: 'long_text',
            label: `${subject} — source of the funds being invested`,
            why: FUNDS_WHY,
            dependsOn,
        },
    ];
}

/** The questions of the section, in the order of the form. */
export const SOURCE_OF_WEALTH_QUESTIONS: readonly SourceOfWealthQuestion[] = [
    {
        id: 'sow_types',
        type: 'multi_select',
        label: 'Sources of wealth',
        why: 'We are required to understand and evidence the origin of invested funds.',
        options: [...SOURCE_OF_WEALTH_TYPES],
    },
    ...sourcePair('employment', 'Employment / business', 'Employment / Business'),
    ...sourcePair('savings', 'Savings & investments', 'Savings & Investments'),
    ...sourcePair('inheritance', 'Inheritance / gifts', 'Inheritance / Gifts'),
    ...sourcePair('property', 'Property sale', 'Property Sale'),
    ...sourcePair('other', 'Other', 'Other'),
    {
        id: 'sow_wealth_country',
        type: 'country_select',
        label: 'Country where the wealth was generated',
        why: 'The country of origin is part of our source-of-wealth assessment.',
    },
    {
        id: 'sow_funds_country',
        type: 'country_select',
        label: 'Country the funds are being transferred from',
        why: 'The originating country is part of our source-of-funds checks.',
    },
];
